import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useFilterManagement } from '@/shared/hooks/useFilterManagement';
import { createProvidersApi } from '../api/providersApi';
import { createProvidersService } from '../services/providersService';
import { Provider, ProviderFilters, NewProviderPayload } from '../types/providers.types';
import { SortDirection } from '@/shared/components/DataTable';
import { buildCompaniesQuery } from '@/shared/queryBuilder/companies/companiesQueryBuilder';
import {
    buildProvidersFilterExpression,
    defaultProviderFilters,
    parseProvidersFilterExpression,
} from '../utils/providersFilterUtils';

const EMPTY_PROVIDERS_QUERY_LIMIT = 200;
const FILTERED_PROVIDERS_QUERY_LIMIT = 100000;

export const useProviders = () => {
    const apiClient = useApiClient();
    const providersApi = createProvidersApi(apiClient);
    const providersService = createProvidersService(providersApi);
    const queryClient = useQueryClient();

    const filterManagement = useFilterManagement<ProviderFilters>({
        targetModel: 'providers',
        defaultFilters: defaultProviderFilters,
        filterToParams: buildProvidersFilterExpression,
        paramsToFilter: parseProvidersFilterExpression,
    });

    const [searchParams, setSearchParams] = useSearchParams();

    const [isSidebarOpen, setSidebarOpen] = useState(true);
    // Pending filters - what user sees in the UI
    const [pendingFilters, setPendingFilters] = useState<ProviderFilters>(filterManagement.filters);
    // Applied filters — derived from URL via useFilterManagement (source of truth).
    const appliedFilters = filterManagement.appliedFilters;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // sortConfig is derived from URL search params so it persists across back/forward navigation.
    // useFilterManagement already preserves 'sort_field' and 'sort_dir' in URL_STATE_KEYS when
    // filter operations (apply / reset / apply-saved-filterset) run.
    const sortConfig = useMemo<{ key: keyof Provider; direction: SortDirection }>(() => {
        const key = (searchParams.get('sort_field') ?? 'name') as keyof Provider;
        const direction = (searchParams.get('sort_dir') ?? SortDirection.Ascending) as SortDirection;
        return { key, direction };
    }, [searchParams]);

    const [isSelectorOpen, setSelectorOpen] = useState(false);
    const [isNewProviderModalOpen, setNewProviderModalOpen] = useState(false);
    const shouldAutoApplySearchRef = useRef(false);

    // Local search term for the input — not trimmed, not URL-derived.
    // Prevents spaces from being stripped when the user types (the URL round-trip
    // in sanitizeQueryParamsForNavigation trims trailing/leading spaces).
    const [localSearchTerm, setLocalSearchTerm] = useState(() => appliedFilters.search || '');

    // Flag to skip the URL-sync effect when the user triggered the URL change themselves.
    const searchChangedByUserRef = useRef(false);

    useEffect(() => {
        setPendingFilters(filterManagement.filters);
    }, [filterManagement.filters]);

    // Sync localSearchTerm from URL only for external changes (reset, saved filterset, etc.)
    // but NOT when the user just typed (which would strip trailing spaces).
    useEffect(() => {
        if (searchChangedByUserRef.current) {
            searchChangedByUserRef.current = false;
            return;
        }
        setLocalSearchTerm(appliedFilters.search || '');
    }, [appliedFilters.search]);

    const managedSearch = filterManagement.filters.search;

    useEffect(() => {
        if (!shouldAutoApplySearchRef.current) {
            return;
        }

        shouldAutoApplySearchRef.current = false;
        filterManagement.applyFilters();
    }, [filterManagement.applyFilters, managedSearch]);

    // searchTerm is the raw value shown in the input (not URL-trimmed).
    const searchTerm = localSearchTerm;
    const query = useMemo(() => buildCompaniesQuery(appliedFilters), [appliedFilters]);


    const { data, error, isLoading, refetch } = useQuery({
        queryKey: ['providers', { page, limit: rowsPerPage, sortConfig, query, ...appliedFilters }],
        queryFn: () => providersService.getProviders({ page, rowsPerPage, sortConfig, query }),
    });

    const createProviderMutation = useMutation({
        mutationFn: (providerData: NewProviderPayload) => providersService.createProvider(providerData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] });
        },
    });

    const providersData = useMemo(() => data?.results ?? [], [data]);
    const totalCount = useMemo(() => data?.total ?? 0, [data]);

    const sortedProviders = useMemo(() => {
        if (!providersData) return [];
        return [...providersData];
    }, [providersData]);

    const handleFilterChange = (filterName: keyof ProviderFilters, value: unknown) => {
        setPendingFilters(prev => ({ ...prev, [filterName]: value }));
        filterManagement.setFilter(filterName, value);
    };

    const applyFilters = () => {
        filterManagement.applyFilters();
        setPage(0);
    };

    const resetFilters = () => {
        setPendingFilters(defaultProviderFilters);
        filterManagement.resetFilters();
        setPage(0);
    };

    const handleSearchChange = useCallback((value: string) => {
        // Update the display value immediately (raw, not trimmed).
        searchChangedByUserRef.current = true;
        setLocalSearchTerm(value);
        setPendingFilters(prev => ({ ...prev, search: value }));
        shouldAutoApplySearchRef.current = true;
        filterManagement.setFilter('search', value);
        setPage(0);
    }, [filterManagement]);

    const toggleSidebar = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    const requestSort = (key: keyof Provider) => {
        let direction = SortDirection.Ascending;
        if (sortConfig.key === key && sortConfig.direction === SortDirection.Ascending) {
            direction = SortDirection.Descending;
        }
        setSearchParams(prev => {
            prev.set('sort_field', String(key));
            prev.set('sort_dir', direction);
            return prev;
        });
        setPage(0);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const exportAllProviders = async (): Promise<Provider[]> => {
        const allData = await providersService.getProviders({
            page: 0,
            rowsPerPage: query.length === 0 ? EMPTY_PROVIDERS_QUERY_LIMIT : FILTERED_PROVIDERS_QUERY_LIMIT,
            sortConfig,
            query,
        });
        return allData.results;
    };


    const handleChangeGroup = () => setSelectorOpen(true);
    const handleCloseSelector = () => setSelectorOpen(false);
    const handleSelectGroup = () => {
        // Selected group
        handleCloseSelector();
    };

    const handleEditClick = () => {
        // Editing provider with ID
    };

    const openNewProviderModal = () => setNewProviderModalOpen(true);
    const closeNewProviderModal = () => setNewProviderModalOpen(false);

    const hasActiveFilters = useMemo(() => {
        return (
            searchTerm.trim().length > 0 ||
            appliedFilters.provider_type !== defaultProviderFilters.provider_type ||
            JSON.stringify(appliedFilters.service_areas) !== JSON.stringify(defaultProviderFilters.service_areas)
        );
    }, [appliedFilters, searchTerm]);
    
    return {
        isSidebarOpen,
        filters: pendingFilters,
        searchTerm,
        providers: sortedProviders,
        totalCount,
        isLoading,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        error,
        isSelectorOpen,
        sortConfig,
        handleFilterChange,
        resetFilters,
        applyFilters,
        toggleSidebar,
        handleSearchChange,
        handleChangeGroup,
        handleCloseSelector,
        handleSelectGroup,
        handleEditClick,
        requestSort,
        handleRowsPerPageChange,
        exportAllProviders,
        isNewProviderModalOpen,
        openNewProviderModal,
        closeNewProviderModal,
        createProvider: createProviderMutation.mutateAsync,
        checkProviderExists: providersService.checkProviderExists,
        checkExternalProviderTypeUnique: providersService.checkExternalProviderTypeUnique,
        refetchProviders: refetch,
        // Saved filters functionality
        saveFilterset: filterManagement.saveFilterset,
        updateFilterset: filterManagement.updateFilterset,
        applySavedFilterset: filterManagement.applySavedFilterset,
        deleteFilterset: filterManagement.deleteFilterset,
        savedFilters: filterManagement.savedFilters,
        isLoadingSavedFilters: filterManagement.isLoadingSavedFilters,
        activeSavedFilterId: filterManagement.activeSavedFilterId,
        currentFilterExpression: filterManagement.currentFilterExpression,
        hasActiveFilters,
    };
};
